// api.js - Service for making API calls to the backend

// Base URL for the API
// Prefer relative path when served behind the same domain via nginx proxy
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const APP_VERSION = process.env.REACT_APP_VERSION || '0.0.0';
const PLATFORM = 'web_admin';
/**
 * Helper function to make API requests
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @returns {Promise} - Promise with the response
 */
export const apiRequest = async (endpoint, options = {}) => {
  // Get the Firebase token from localStorage
  const token = localStorage.getItem('authToken');

  // Set default headers
  const headers = {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION,
    'X-Platform': PLATFORM,
    ...options.headers,
  };

  // Add authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return null;
    }

    // Parse JSON response if the request was successful
    if (response.ok) {
      // Check if response is empty
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }

    // Handle error responses — surface the backend's message body when present,
    // appended after an em-dash so callers can strip the "API Error: <code> ..." prefix.
    let backendMessage = '';
    let backendCode = '';
    let backendDetails;
    try {
      const errorText = await response.text();
      if (errorText) {
        try {
          const parsed = JSON.parse(errorText);
          backendMessage = parsed.message || parsed.error || '';
          // Typed refusals carry a machine-readable code (e.g. REFUND_REJECTED).
          backendCode = typeof parsed.code === 'string' ? parsed.code : '';
          // Bean-validation 400s carry one message per violated field in `details`.
          if (Array.isArray(parsed.details)) backendDetails = parsed.details;
        } catch {
          backendMessage = errorText;
        }
      }
    } catch {
      // ignore body read failures
    }
    const suffix = backendMessage ? ` — ${backendMessage}` : '';
    const apiError = new Error(`API Error: ${response.status} ${response.statusText}${suffix}`);
    // Message shape is unchanged (callers strip the prefix), but the status is attached so a
    // caller can branch on it — e.g. 409 CONFLICT when a provider carries Provider Terms
    // acceptances and must be anonymized rather than hard-deleted.
    apiError.status = response.status;
    apiError.backendMessage = backendMessage;
    apiError.backendCode = backendCode;
    if (backendDetails) apiError.details = backendDetails;
    throw apiError;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

// Settings API
export const settingsApi = {
  listAll: () => apiRequest('/settings'),
  getByKey: (key) => apiRequest(`/settings/${encodeURIComponent(key)}`),
  upsert: (key, value, type = 'TEXT') => apiRequest(`/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({ value, type }),
  }),
  delete: (key) => apiRequest(`/settings/${encodeURIComponent(key)}`, { method: 'DELETE' }),
};

// User API
export const userApi = {
  // Get current user
  getCurrentUser: () => apiRequest('/users/me'),

  // Get all users (pageable)
  getAllUsers: ({ page = 0, size = 20, sort } = {}) => {
    const params = new URLSearchParams({ page, size });
    if (sort) params.set('sort', sort);
    return apiRequest(`/users?${params.toString()}`);
  },

  // Search users by country (pageable)
  searchUsersByCountry: ({ country, page = 0, size = 20, sort }) => {
    const params = new URLSearchParams({ country, page, size });
    if (sort) params.set('sort', sort);
    return apiRequest(`/users/search/by-country?${params.toString()}`);
  },

  // Search users by city (pageable)
  searchUsersByCity: ({ city, page = 0, size = 20, sort }) => {
    const params = new URLSearchParams({ city, page, size });
    if (sort) params.set('sort', sort);
    return apiRequest(`/users/search/by-city?${params.toString()}`);
  },

  // Get user by ID — admin full projection (includes PII + legal-consent fields).
  // The plain GET /users/{id} returns a PII-free projection that omits consent.
  getUserById: (id) => apiRequest(`/users/${id}/full`),

  // Update user
  updateUser: (id, userData) => apiRequest(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),

  // Delete user
  deleteUser: (id) => apiRequest(`/users/${id}`, {
    method: 'DELETE',
  }),

  // Search users by name
  searchUsersByName: (name) => apiRequest(`/users/search?name=${encodeURIComponent(name)}`),

  // Admin: search users by name OR email (case-insensitive contains, pageable)
  searchUsersByNameOrEmail: ({ q, page = 0, size = 20, sort } = {}) => {
    const params = new URLSearchParams({ q, page, size });
    if (sort) params.set('sort', sort);
    return apiRequest(`/users/search/by-name-or-email?${params.toString()}`);
  },

  // Add admin role to user
  addAdminRole: (id) => apiRequest(`/users/${id}/roles/admin`, {
    method: 'POST',
  }),

  // Add provider role to user
  addProviderRole: (id) => apiRequest(`/users/${id}/roles/provider`, {
    method: 'POST',
  }),

  suspend: (id) => apiRequest(`/users/${id}/suspend`, {
    method: 'POST',
  }),

  unsuspend: (id) => apiRequest(`/users/${id}/unsuspend`, {
    method: 'POST',
  }),

  // Get user claims (including roles)
  getUserClaims: (authId) => apiRequest(`/users/claims/${authId}`),
};

// Dogs API
export const dogsApi = {
  // breedAttributeId and energyAttributeId are Long IDs from the dog_attributes table.
  adminSearch: ({ search, breedAttributeId, energyAttributeId, countryId, cityId, page = 0, size = 20 } = {}) => {
    const params = new URLSearchParams({ page, size });
    if (search)            params.set('search',            search);
    if (breedAttributeId)  params.set('breedAttributeId',  breedAttributeId);
    if (energyAttributeId) params.set('energyAttributeId', energyAttributeId);
    if (countryId)         params.set('countryId',         countryId);
    if (cityId)            params.set('cityId',            cityId);
    return apiRequest(`/dogs/admin/search?${params.toString()}`);
  },
  // City dropdown options: only cities where a dog's owner lives.
  getFilterCities: (countryId = null) =>
    apiRequest(`/dogs/admin/filter-cities${countryId ? `?countryId=${countryId}` : ''}`),
  getAdminById: (id) => apiRequest(`/dogs/admin/${id}`),
};

// Dog Owner API
export const dogOwnerApi = {
  // Get all dog owner profiles
  getAllProfiles: () => apiRequest('/owners/all'),

  // Get dog owner profile by ID
  getProfileById: (id) => apiRequest(`/owners/${id}`),

  // Update dog owner profile
  updateProfile: (id, profileData) => apiRequest(`/owners/${id}`, {
    method: 'PUT',
    body: JSON.stringify(profileData),
  }),

  // Delete dog owner profile
  deleteProfile: (id) => apiRequest(`/owners/${id}`, {
    method: 'DELETE',
  }),
};

// Dog Gardens API
export const dogGardensApi = {
  // Get all dog gardens
  getAllGardens: () => apiRequest('/gardens'),

  // Get all dog gardens with pagination
  getAllGardensPaginated: (page = 0, size = 20, sort = null, direction = 'ASC', visible = null) => {
    let url = `/gardens/paginated?page=${page}&size=${size}`;
    if (sort) {
      url += `&sort=${sort}&direction=${direction}`;
    }
    if (visible !== null && visible !== undefined) {
      url += `&visible=${visible}`;
    }
    return apiRequest(url);
  },

  // Admin flexible garden search (all params optional)
  adminSearchGardens: ({ query, city, country, visible, source, page = 0, size = 20, sort, direction = 'ASC' } = {}) => {
    const params = new URLSearchParams({ page, size, direction });
    if (query)   params.set('query',   query);
    if (city)    params.set('city',    city);
    if (country) params.set('country', country);
    if (visible !== null && visible !== undefined) params.set('visible', visible);
    if (source)  params.set('source',  source);
    if (sort)    params.set('sort',    sort);
    return apiRequest(`/gardens/admin/search?${params.toString()}`);
  },

  // Admin: list all removal reports for a single garden, newest first
  getRemovalReports: (gardenId) => apiRequest(`/gardens/${gardenId}/removal-reports`),

  // Admin: {gardenId -> openReportCount} for every garden with at least one OPEN report
  getOpenRemovalReportCounts: () => apiRequest('/gardens/admin/open-removal-report-counts'),

  // Get dog garden by ID (admin view — includes hidden photos so they can be re-enabled)
  getGardenById: (id) => apiRequest(`/gardens/admin/${id}`),

  // Create dog garden
  createGarden: (gardenData) => apiRequest('/gardens', {
    method: 'POST',
    body: JSON.stringify(gardenData),
  }),

  // Update dog garden
  updateGarden: (id, gardenData) => apiRequest(`/gardens/${id}`, {
    method: 'PUT',
    body: JSON.stringify(gardenData),
  }),

  // Delete dog garden
  deleteGarden: (id) => apiRequest(`/gardens/${id}`, {
    method: 'DELETE',
  }),

  // Add a photo to a dog garden
  addPhotoToGarden: (gardenId, photoUrl) => apiRequest(`/gardens/${gardenId}/photos`, {
    method: 'POST',
    body: JSON.stringify({ photoUrl }),
  }),

  // Remove a photo from a dog garden
  removePhotoFromGarden: (gardenId, photoId) => apiRequest(`/gardens/${gardenId}/photos/${photoId}`, {
    method: 'DELETE',
  }),

  // Toggle photo visibility
  togglePhotoVisibility: (gardenId, photoId) => apiRequest(`/gardens/${gardenId}/photos/${photoId}/toggle-visibility`, {
    method: 'PUT',
  }),

  // Reorder garden photos — orderedPhotoIds must list ALL photo ids in the desired order
  reorderGardenPhotos: (gardenId, orderedPhotoIds) => apiRequest(`/gardens/${gardenId}/photos/order`, {
    method: 'PUT',
    body: JSON.stringify({ orderedPhotoIds }),
  }),

  // Get followers for a garden (admin use)
  getFollowers: (gardenId) => apiRequest(`/gardens/${gardenId}/followers`),

  // Admin: preview how many gardens will be soft-hidden by city/country filter
  previewBulkHide: ({ city, country } = {}) => {
    const params = new URLSearchParams();
    if (city)    params.set('city',    city);
    if (country) params.set('country', country);
    return apiRequest(`/gardens/admin/bulk-hide/preview?${params.toString()}`);
  },

  // Admin: soft-hide all gardens matching city/country filter
  bulkHide: ({ city, country }) => apiRequest('/gardens/admin/bulk-hide', {
    method: 'POST',
    body: JSON.stringify({ city: city || null, country: country || null }),
  }),

  // Admin: hard-delete all gardens matching city/country filter.
  // Gardens with FK references are skipped and reported.
  bulkDelete: ({ city, country }) => apiRequest('/gardens/admin/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ city: city || null, country: country || null }),
  }),

  // Admin: preview how many currently-hidden gardens would be published
  previewBulkPublish: ({ city, country } = {}) => {
    const params = new URLSearchParams();
    if (city)    params.set('city',    city);
    if (country) params.set('country', country);
    return apiRequest(`/gardens/admin/bulk-publish/preview?${params.toString()}`);
  },

  // Admin: publish (set visible=true) all currently-hidden gardens matching filter
  bulkPublish: ({ city, country }) => apiRequest('/gardens/admin/bulk-publish', {
    method: 'POST',
    body: JSON.stringify({ city: city || null, country: country || null }),
  }),

  // Admin: bulk-import from dog-park-finder JSON file (multipart upload)
  bulkImportFromJson: async (file, conflictRadiusMeters = 100) => {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ conflictRadiusMeters: String(conflictRadiusMeters) });
    const response = await fetch(`${API_BASE_URL}/gardens/admin/bulk-import?${params.toString()}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return null;
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Import failed: ${response.status} ${response.statusText} — ${text}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  },

  // Admin: distinct countries currently in the gardens table
  getDistinctCountries: () => apiRequest('/gardens/admin/distinct-countries'),

  // Admin: distinct cities (optionally scoped to a country)
  getDistinctCities: (country = null) => {
    const params = new URLSearchParams();
    if (country) params.set('country', country);
    const qs = params.toString();
    return apiRequest(`/gardens/admin/distinct-cities${qs ? `?${qs}` : ''}`);
  },
};

// Posts API
export const postsApi = {
  // Get all posts
  getAllPosts: () => apiRequest('/posts'),

  // Get all posts with pagination
  getAllPostsPaginated: (page = 0, size = 20) => 
    apiRequest(`/posts/all?page=${page}&size=${size}`),

  // Get post by ID
  getPostById: (id) => apiRequest(`/posts/${id}`),

  // Create post
  createPost: (postData) => apiRequest('/posts', {
    method: 'POST',
    body: JSON.stringify(postData),
  }),

  // Update post
  updatePost: (id, postData) => apiRequest(`/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(postData),
  }),

  // Delete post
  deletePost: (id) => apiRequest(`/posts/${id}`, {
    method: 'DELETE',
  }),

  adminSetVisibility: (id, visible) => apiRequest(`/posts/${id}/visibility`, {
    method: 'PATCH',
    body: JSON.stringify({ visible }),
  }),

  // Admin: Update post fields
  updatePostAdmin: (id, payload) => apiRequest(`/posts/admin/${id}` , {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),

  // Admin: get all posts authored by a user (any type/visibility, excludes deleted)
  getPostsByUserAdmin: (userId, { page = 0, size = 20 } = {}) => {
    const params = new URLSearchParams({ page, size });
    return apiRequest(`/posts/admin/user/${userId}?${params.toString()}`);
  },

  // Admin flexible search (all params optional)
  adminSearchPosts: ({ gardenId, postType, city, country, page = 0, size = 20 } = {}) => {
    const params = new URLSearchParams({ page, size });
    if (gardenId) params.set('gardenId', gardenId);
    if (postType) params.set('postType', postType);
    if (city) params.set('city', city);
    if (country) params.set('country', country);
    return apiRequest(`/posts/admin/search?${params.toString()}`);
  },

  // Admin: get all comments authored by a user
  getCommentsByUserAdmin: (userId, { page = 0, size = 20 } = {}) => {
    const params = new URLSearchParams({ page, size });
    return apiRequest(`/posts/admin/user/${userId}/comments?${params.toString()}`);
  },

  // Get comments for a specific post (paginated)
  getPostComments: (postId, { page = 0, size = 50 } = {}) => {
    const params = new URLSearchParams({ page, size });
    return apiRequest(`/posts/${postId}/comments?${params.toString()}`);
  },

  // Admin: delete a single post-media image
  deletePostMediaAdmin: (mediaId) => apiRequest(`/posts/admin/media/${mediaId}`, { method: 'DELETE' }),

  // Admin: delete any comment by id
  deleteCommentAdmin: (commentId) => apiRequest(`/posts/admin/comments/${commentId}`, { method: 'DELETE' }),
};

// Admin User Notes API
export const adminUserNotesApi = {
  list: (userId, { page = 0, size = 20 } = {}) => {
    const params = new URLSearchParams({ page, size });
    return apiRequest(`/admin/users/${userId}/notes?${params.toString()}`);
  },
  create: (userId, note) => apiRequest(`/admin/users/${userId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  }),
  update: (noteId, note) => apiRequest(`/admin/users/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify({ note }),
  }),
  delete: (noteId) => apiRequest(`/admin/users/notes/${noteId}`, {
    method: 'DELETE',
  }),
};

// Providers API
export const providersApi = { 
  // --- Reviews moderation (verified-provider-reviews design §7) ---
  //
  // This read INCLUDES hidden rows. Every other review read in the product filters them; this
  // one must not, because the admin tab is the only screen able to inspect a hide's audit trail
  // or restore the review -- filtering here would make a hide irreversible.
  getProviderReviewsForAdmin: (providerId) => apiRequest(`/admin/providers/${providerId}/reviews`),

  setReviewVisibility: (reviewId, hidden, reason) => apiRequest(`/admin/reviews/${reviewId}/visibility`, {
    method: 'POST',
    body: JSON.stringify({ hidden, reason }),
  }),

  // Hides the provider's answer alone, leaving a legitimate review visible: an accurate review
  // can draw an abusive reply, and hiding the review to suppress it punishes the wrong party.
  setReviewReplyVisibility: (reviewId, hidden, reason) => apiRequest(`/admin/reviews/${reviewId}/reply/visibility`, {
    method: 'POST',
    body: JSON.stringify({ hidden, reason }),
  }),

  // Services visibility management
  listServicesVisibility: () => apiRequest('/providers/services-visibility'),
  upsertServicesVisibility: ({ country, city = null, enabled, live = true }) => apiRequest('/providers/services-visibility', {
    method: 'PUT',
    body: JSON.stringify({ country, city, enabled, live }),
  }),
  bulkSetCountryVisibility: ({ country, enabled, live = true }) => apiRequest('/providers/services-visibility/bulk', {
    method: 'PUT',
    body: JSON.stringify({ country, enabled, live }),
  }),
  bulkSetCountryLive: ({ country, live }) => apiRequest('/providers/services-visibility/bulk-live', {
    method: 'PUT',
    body: JSON.stringify({ country, live }),
  }),

  // Store visibility management
  listStoreVisibility: () => apiRequest('/providers/store-visibility'),
  upsertStoreVisibility: ({ country, enabled }) => apiRequest('/providers/store-visibility', {
    method: 'PUT',
    body: JSON.stringify({ country, enabled }),
  }),
  
  // Get all providers
  getAllProviders: () => apiRequest('/providers'),

  // Get all providers with pagination and optional search filters
  getAllProvidersPaginated: (page = 0, size = 20, sort = null, direction = 'DESC', state = null, filters = {}) => {
    const params = new URLSearchParams({ page, size });
    // sort/direction were built here before but the endpoint ignored them and the caller always
    // passed null. They are real now -- ProviderSortKeys allowlists the key server-side.
    if (sort) params.set('sort', sort);
    if (direction) params.set('direction', direction);
    if (state) params.set('state', state);
    if (filters.name) params.set('name', filters.name);
    if (filters.countryId) params.set('countryId', filters.countryId);
    if (filters.cityId) params.set('cityId', filters.cityId);
    if (filters.serviceType) params.set('serviceType', filters.serviceType);
    if (filters.providerType) params.set('providerType', filters.providerType);
    if (filters.intakeSource) params.set('intakeSource', filters.intakeSource);
    if (filters.visibility) params.set('visibility', filters.visibility);
    // Claim state is tri-state: 'true' | 'false' | omitted (= all). Compared against undefined
    // so the 'false' (unclaimed) case isn't swallowed by a falsy check.
    if (filters.claimed !== undefined && filters.claimed !== null && filters.claimed !== '') {
      params.set('claimed', filters.claimed);
    }
    return apiRequest(`/providers/paginated?${params.toString()}`);
  },

  // City dropdown options: only cities that have a provider (business or owner city).
  getFilterCities: (countryId = null) =>
    apiRequest(`/providers/admin/filter-cities${countryId ? `?countryId=${countryId}` : ''}`),

  // Get provider by ID (admin scope). The public /providers/{id} 404s a seeded profile that
  // isn't listable yet — which is every freshly imported one — so admin screens must use this.
  // Adds publicEmail / consentStatus / listable on top of the public payload.
  getProviderById: (id) => apiRequest(`/admin/providers/${id}`),

  // Create provider
  createProvider: (providerData) => apiRequest('/providers', {
    method: 'POST',
    body: JSON.stringify(providerData),
  }),

  // Admin edit. Partial by design — every omitted field is left unchanged, so this can't wipe
  // images or services the way a full PUT /providers/{id} does (that one clears both lists from
  // the DTO's empty defaults). Send "" to clear a scalar, [] to clear a list.
  adminUpdateProvider: (id, payload) => apiRequest(`/admin/providers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),

  adminAddService: (id, service) => apiRequest(`/admin/providers/${id}/services`, {
    method: 'POST',
    body: JSON.stringify(service),
  }),

  adminRemoveService: (id, serviceId) =>
    apiRequest(`/admin/providers/${id}/services/${serviceId}`, { method: 'DELETE' }),

  // Uploads one image and returns its stored `name` — that name (not a URL) is what goes into
  // the provider's images list. Multipart, so it bypasses apiRequest's JSON content type.
  uploadImage: async (file) => {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/images/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return null;
    }
    const text = await response.text();
    const body = text ? JSON.parse(text) : {};
    if (!response.ok || body.error) {
      throw new Error(body.error || `Upload failed: ${response.status} ${response.statusText}`);
    }
    return body.name;
  },

  // Update provider
  updateProvider: (id, providerData) => apiRequest(`/providers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(providerData),
  }),

  // Delete provider
  deleteProvider: (id) => apiRequest(`/providers/${id}`, {
    method: 'DELETE',
  }),

  // Approve provider
  approveProvider: (id) => apiRequest(`/providers/${id}/state`, {
    method: 'POST',
    body: JSON.stringify({state: 'APPROVED'})
  }),

  // Decline provider
  declineProvider: (id) => apiRequest(`/providers/${id}/state`, {
    method: 'POST',
    body: JSON.stringify({state: 'DECLINED'})
  }),

  // Pro actions (spec §4.5): { action: GRANT|CHANGE_END|REVOKE|OVERRIDE_ON|OVERRIDE_OFF|OVERRIDE_CLEAR,
  //   reason, untilDate?, allowShorten?, clearOverride? }
  proPlanAction: (id, payload) => apiRequest(`/providers/${id}/pro-plan`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  getCheckoutsNeedingReview: () => apiRequest('/admin/providers/checkouts/needs-review'),
  resolveCheckout: (id, payload) => apiRequest(`/admin/providers/${id}/checkout/resolve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),

  // Landing config
  getLandingConfig: (locale) => apiRequest(`/providers/landing-config?locale=${locale}`),
  upsertLandingConfig: ({ locale, content }) => apiRequest('/providers/landing-config', {
    method: 'PUT',
    body: JSON.stringify({ locale, content }),
  }),

  // Suspend / unsuspend provider (admin only)
  suspendProvider: (id) => apiRequest(`/providers/${id}/suspend`, { method: 'POST' }),
  unsuspendProvider: (id) => apiRequest(`/providers/${id}/unsuspend`, { method: 'POST' }),

  // Get provider orders (admin access)
  getProviderOrders: (id, page = 0, size = 10) =>
    apiRequest(`/providers/${id}/orders?page=${page}&size=${size}`),

  // Admin: create a provider entity for an existing user (bypasses self-service flow)
  // Payload keys match AdminCreateProviderDto (camelCase):
  //   userId, providerType, title, about, instagramUser, yearsOfExperience, documentUrl, serviceTypes
  // ---- seeded providers: consent ledger + claim sending ----
  getConsentEvents: (providerId) => apiRequest(`/admin/providers/${providerId}/consent-events`),
  recordConsentEvent: (providerId, { event, sourceChannel, grantedBy, scope, note }) =>
    apiRequest(`/admin/providers/${providerId}/consent-events`, {
      method: 'POST',
      body: JSON.stringify({ event, sourceChannel, grantedBy, scope, note }),
    }),
  // Admin sends are not rate-limited — the 6h cooldown only guards the public self-service
  // endpoint. `reason` is optional and recorded in the server-side audit log.
  sendSeededClaim: (providerId, { reason = null } = {}) =>
    apiRequest(`/admin/provider-applications/seeded/${providerId}/send-claim`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // Mints a claim link for the admin to deliver out-of-band (Instagram DM, WhatsApp, phone).
  // The returned url is a bearer credential — show it, let the admin copy it, never persist it.
  // Generating supersedes any previously live link for this provider.
  generateSeededClaimLink: (providerId) =>
    apiRequest(`/admin/provider-applications/seeded/${providerId}/claim-link`, { method: 'POST' }),

  createProviderForUser: (payload) => apiRequest('/providers/admin/create-for-user', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
};

// Orders API (admin access)
export const ordersApi = {
  getOrderById: (id) => apiRequest(`/orders/${id}`),
  getOrderMessages: (id) => apiRequest(`/orders/${id}/messages`),
};

// Payment recovery API (booking contract §8).
//
// These are the cases the backend deliberately REFUSES to decide on its own — an unknown PayPal
// outcome, a post-start authorization needing both parties' attestations, an unreported
// completion. Without this surface an operator had to resolve them with raw API calls.
export const paymentReviewApi = {
  // Unresolved queue (OPEN + CLAIMED), oldest first.
  list: (page = 0, size = 50) =>
    apiRequest(`/admin/payment-reviews?page=${page}&size=${size}`),

  // Claiming is what stops two operators working the same item.
  claim: (itemId) => apiRequest(`/admin/payment-reviews/${itemId}/claim`, { method: 'POST' }),

  // `outcome` must be one of the item's own allowedOutcomes — the server re-checks it against
  // the ORDER state too, so a stale queue cannot resolve something that has since moved.
  resolve: (itemId, body) =>
    apiRequest(`/admin/payment-reviews/${itemId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // requestId is the idempotency key: the SAME id must be resent on a retry, or a timeout
  // followed by a retry would refund twice.
  refund: (orderId, { amount, reason, requestId }) =>
    apiRequest(`/admin/orders/${orderId}/refund`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason, requestId }),
    }),
};

// Export a default API object with all the API functions
// Reports API
export const reportsApi = {
  // Get all reports with pagination and optional status filter
  getAllReportsPaginated: (page = 0, size = 20, status = null) => {
    const params = new URLSearchParams({ page, size });
    if (status) params.set('status', status);
    return apiRequest(`/reports?${params.toString()}`);
  },
  // Get reports by post
  getReportsByPost: (postId, page = 0, size = 20) =>
    apiRequest(`/reports/post/${postId}?page=${page}&size=${size}`),
  // Close report
  closeReport: (id) => apiRequest(`/reports/${id}/close`, { method: 'PATCH' }),
};

// Product Categories API
export const productCategoriesApi = {
  // Get all active categories
  getAllCategories: () => apiRequest('/categories'),
  
  // Get root categories (no parent)
  getRootCategories: () => apiRequest('/categories/root'),
  
  // Get category by ID
  getCategoryById: (id) => apiRequest(`/categories/${id}`),
  
  // Get subcategories by parent category ID
  getSubCategories: (parentId) => apiRequest(`/categories/${parentId}/subcategories`),
  
  // Create a new category (Admin only)
  createCategory: (categoryData) => apiRequest('/categories', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  }),
  
  // Update an existing category (Admin only)
  updateCategory: (id, categoryData) => apiRequest(`/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData),
  }),
  
  // Delete a category (Admin only)
  deleteCategory: (id) => apiRequest(`/categories/${id}`, {
    method: 'DELETE',
  }),
  
  // Deactivate a category (Admin only)
  deactivateCategory: (id) => apiRequest(`/categories/${id}/deactivate`, {
    method: 'PATCH',
  }),
};

// Admin Insights API (dashboard analytics)
export const insightsApi = {
  getOverview:        () => apiRequest('/admin/insights/overview'),
  getTopCountries:    (limit = 10) => apiRequest(`/admin/insights/top-countries?limit=${limit}`),
  getTopActiveUsers:  (days = 7, limit = 10) =>
    apiRequest(`/admin/insights/top-active-users?days=${days}&limit=${limit}`),
  getTopGardens:       (limit = 10) => apiRequest(`/admin/insights/top-gardens?limit=${limit}`),
  getDogDistributions: () => apiRequest('/admin/insights/dog-distributions'),
  getUserGrowth:       (days = 30) => apiRequest(`/admin/insights/user-growth?days=${days}`),
};

// Moderation Submissions API (unified Submissions hub)
export const moderationApi = {
  // List submissions (paginated). All filters optional.
  // status: 'SUBMITTED' | 'RESOLVED'
  // type:   'GARDEN_REQUEST' | 'PHOTO_SUGGESTION' | 'POST_REPORT' | 'PROVIDER_APPLICATION'
  listSubmissions: ({ status, type, page = 0, size = 20 } = {}) => {
    const params = new URLSearchParams({ page, size });
    if (status) params.set('status', status);
    if (type) params.set('type', type);
    return apiRequest(`/admin/moderation/submissions?${params.toString()}`);
  },

  // Pending counts per type, plus total. Drives dashboard badge and tab badges.
  getCounts: () => apiRequest('/admin/moderation/counts'),

  // Resolve a pending submission. resolution: 'APPROVED' | 'REJECTED' | 'DISMISSED'.
  resolve: (id, resolution, note) => apiRequest(`/admin/moderation/submissions/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution, note: note || null }),
  }),
};

// Admin escape hatches for the public provider-application claim flow.
export const providerApplicationApi = {
  // List all public provider applications (paginated), optionally filtered by status.
  // status: 'PENDING' | 'APPROVED' | 'CLAIMED' | 'SUPERSEDED' | 'REJECTED'
  list: ({ page = 0, size = 20, status } = {}) => {
    const params = new URLSearchParams({ page, size });
    if (status) params.append('status', status);
    return apiRequest(`/admin/provider-applications?${params.toString()}`);
  },

  // Get a single application by id — full ProviderApplicationAdminDto.
  get: (id) => apiRequest(`/admin/provider-applications/${id}`),

  // Re-send and re-mint are the same action: only the token hash is stored, so the
  // original raw token is unrecoverable and "send the same link again" is impossible.
  // This always issues a fresh link and invalidates the previous one.
  resendClaimLink: (applicationId) =>
    apiRequest(`/admin/provider-applications/${applicationId}/resend`, { method: 'POST' }),

  // Last resort for an applicant who signed up under a different address and lost their
  // link. Runs the same claim transaction as the self-service routes.
  claimForUser: (applicationId, userId) =>
    apiRequest(`/admin/provider-applications/${applicationId}/claim-for-user`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
};

// City Manager Admin API
export const cityManagerAdminApi = {
  getManagedCities: (userId) => apiRequest(`/admin/city-managers?userId=${userId}`),
  assignCity: (cityId, userId) => apiRequest('/admin/city-managers', {
    method: 'POST',
    body: JSON.stringify({ userId, cityId }),
  }),
  unassignCity: (cityId, userId) => apiRequest(`/admin/city-managers/${userId}/cities/${cityId}`, {
    method: 'DELETE',
  }),
};

// App Version Config API
export const appVersionConfigApi = {
  listAll: () => apiRequest('/app-config/version/all'),
  update: (platform, payload) => apiRequest(`/app-config/version/${encodeURIComponent(platform)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),
  getBackendVersion: () => apiRequest('/app-config/backend-version'),
};

/** Public runtime switches, the same endpoint the app reads at launch. */
export const runtimeConfigApi = {
  get: () => apiRequest('/app-config'),
};

// Legal Docs API
export const legalDocsApi = {
  listAll: () => apiRequest('/legal-docs/all'),
  getById: (id) => apiRequest(`/legal-docs/${id}`),
  create: (data) => apiRequest('/legal-docs', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/legal-docs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/legal-docs/${id}`, { method: 'DELETE' }),
};

/**
 * The ONLY way to activate Provider Terms. Publishing binds every provider to the contract and
 * blocks those who have not accepted, so the UI confirms before calling this.
 */
export const providerTermsApi = {
  publish: (version) =>
    apiRequest('/admin/provider-terms/publish', {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),
};

// Store commission (default schedule + per-store overrides). Percentages are plain numbers (10 = 10%).
export const storeCommissionApi = {
  getDefault: () => apiRequest('/admin/store-commission/default'),
  updateDefault: (schedule) => apiRequest('/admin/store-commission/default', {
    method: 'PUT',
    body: JSON.stringify(schedule),
  }),
  getForProvider: (providerId) => apiRequest(`/admin/providers/${providerId}/commission-schedule`),
  updateForProvider: (providerId, schedule) => apiRequest(`/admin/providers/${providerId}/commission-schedule`, {
    method: 'PUT',
    body: JSON.stringify(schedule),
  }),
  // Reverts the store to the platform default.
  revertForProvider: (providerId) => apiRequest(`/admin/providers/${providerId}/commission-schedule`, {
    method: 'DELETE',
  }),
};

// Escalated store returns + late shipments. Amounts are plain numbers in the order's currency.
export const storeReturnsApi = {
  // statuses: array of StoreReturnStatus; the server defaults to ESCALATED, oldest first.
  list: ({ statuses, page = 0, size = 50 } = {}) => {
    const params = new URLSearchParams({ page, size });
    if (statuses && statuses.length) params.set('status', statuses.join(','));
    return apiRequest(`/admin/store-returns?${params.toString()}`);
  },
  get: (returnId) => apiRequest(`/admin/store-returns/${returnId}`),
  // body: { decision: 'approve'|'reject', amount?, restock?, note? }. Final. Re-sending the same
  // call after an unknown outcome (500) is safe: the refund is idempotent per return.
  decide: (returnId, body) => apiRequest(`/admin/store-returns/${returnId}/decide`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  lateShipments: ({ page = 0, size = 50 } = {}) =>
    apiRequest(`/admin/store-fulfilment/late-shipments?page=${page}&size=${size}`),
};

// Held store payouts (list, release / hold / resume / retry, payout + refund-fee settings).
// Money is a plain number in the order's currency; instants are ISO-8601 UTC.
export const storePayoutsApi = {
  // statuses: array of payout statuses (sent as a repeated `status` param); flag/providerId optional.
  list: ({ statuses, providerId, flag, page = 0, size = 50 } = {}) => {
    const params = new URLSearchParams({ page, size });
    (statuses || []).forEach((s) => params.append('status', s));
    if (providerId !== undefined && providerId !== null && providerId !== '') params.set('providerId', providerId);
    if (flag) params.set('flag', flag);
    return apiRequest(`/admin/store-payouts?${params.toString()}`);
  },
  get: (orderId) => apiRequest(`/admin/store-payouts/${orderId}`),
  // Each action answers the updated payout. release / retry reuse the server's stable idempotency
  // key, so repeating after an unknown outcome (5xx / no response) is safe.
  release: (orderId) => apiRequest(`/admin/store-payouts/${orderId}/release`, { method: 'POST' }),
  hold: (orderId) => apiRequest(`/admin/store-payouts/${orderId}/hold`, { method: 'POST' }),
  resume: (orderId) => apiRequest(`/admin/store-payouts/${orderId}/resume`, { method: 'POST' }),
  retry: (orderId) => apiRequest(`/admin/store-payouts/${orderId}/retry`, { method: 'POST' }),
  getSettings: () => apiRequest('/admin/store-payouts/settings'),
  // body: { returnWindowDays, bufferDays, refundAdminFeePercent }
  updateSettings: (body) => apiRequest('/admin/store-payouts/settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  }),
};

// AI Usage Admin API
export const aiUsageApi = {
  getUsage: (userId) => apiRequest(`/ai/admin/usage/${encodeURIComponent(userId)}`),
  resetUsage: (userId) => apiRequest(`/ai/admin/usage/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
};

// Export a default API object with all the API functions
export default {
  user: userApi,
  dogs: dogsApi,
  dogOwner: dogOwnerApi,
  dogGardens: dogGardensApi,
  posts: postsApi,
  providers: providersApi,
  reports: reportsApi,
  productCategories: productCategoriesApi,
  legalDocs: legalDocsApi,
  providerTerms: providerTermsApi,
  adminUserNotes: adminUserNotesApi,
  moderation: moderationApi,
  providerApplication: providerApplicationApi,
  insights: insightsApi,
  appVersionConfig: appVersionConfigApi,
  cityManager: cityManagerAdminApi,
  storeCommission: storeCommissionApi,
  storeReturns: storeReturnsApi,
  storePayouts: storePayoutsApi,
};

// ---- Seeded provider Excel import ------------------------------------------------

export const providerImportApi = {
  validate: (fileHash, rows) =>
    apiRequest('/admin/provider-import/validate', {
      method: 'POST',
      body: JSON.stringify({ fileHash, rows }),
    }),

  commit: (batchId, idempotencyKey, approvedRowIndexes) =>
    apiRequest(`/admin/provider-import/batches/${batchId}/commit`, {
      method: 'POST',
      body: JSON.stringify({ idempotencyKey, approvedRowIndexes }),
    }),

  // Quarantine thumbnails need the Authorization header — <img src> can't send it,
  // so fetch a blob and hand back an object URL (callers revoke it when done).
  thumbnailBlobUrl: async (batchId, rowIndex, imageIndex) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/provider-import/batches/${batchId}/rows/${rowIndex}/images/${imageIndex}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch (e) {
      return null;
    }
  },
};
